// Silent recovery for application create/update submits.
//
// The funnel intentionally shows no error UI on a failed save (see
// ApplicationForm.js — failures land in Datadog RUM only), which historically
// stranded applicants whose final submit failed in flaky in-app browsers:
// the PATCH died, nothing happened on screen, and the application stayed at
// step 5 forever (STX-746). Instead of surfacing errors, we retry transient
// failures and resync state so a re-click of the still-enabled submit button
// can succeed — or is recognized as already having succeeded.
//
// redaxios rejects with the Response-like object for HTTP errors (has a
// numeric `status`) and with a bare TypeError for network failures.

type SleepFn = (ms: number) => Promise<void>

const defaultSleep: SleepFn = ms =>
  new Promise(resolve => setTimeout(resolve, ms))

export function submitStatus(err: unknown): number | undefined {
  if (
    err !== null &&
    typeof err === 'object' &&
    'status' in err &&
    typeof (err as {status: unknown}).status === 'number'
  ) {
    return (err as {status: number}).status
  }
  return undefined
}

export function isNetworkError(err: unknown): boolean {
  return submitStatus(err) === undefined
}

function isRetryable(err: unknown): boolean {
  const status = submitStatus(err)
  return status === undefined || status >= 500
}

export async function withSubmitRetry<T>(
  submit: () => Promise<T>,
  {retries = 2, baseDelayMs = 500, sleep = defaultSleep} = {},
): Promise<T> {
  let lastError: unknown
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      await sleep(baseDelayMs * 2 ** (attempt - 1))
    }
    try {
      return await submit()
    } catch (err) {
      lastError = err
      if (!isRetryable(err)) {
        throw err
      }
    }
  }
  throw lastError
}

type ApplicationLike = {
  id: string
  termId?: string | null
  form?: {step?: number | null} | null
}

export const FINAL_STEP = 6

export async function submitApplicationWithRecovery<T extends ApplicationLike>({
  submit,
  isFinalStep,
  appId,
  termId,
  getApps,
  onAppsChanged,
  retries,
  sleep,
}: {
  submit: () => Promise<T>
  isFinalStep: boolean
  /** The application being updated, or 'new' for a create. */
  appId: string
  /** The term the submit targets — used to adopt an existing app on create conflicts. */
  termId?: string | null
  getApps: () => Promise<T[]>
  /** Called when local application state is stale (409/404) so the caller can refetch. */
  onAppsChanged?: () => void
  retries?: number
  sleep?: SleepFn
}): Promise<T> {
  try {
    return await withSubmitRetry(submit, {retries, sleep})
  } catch (err) {
    const status = submitStatus(err)
    const isCreate = appId === 'new'

    // A final submit can commit server-side while the response is lost: the
    // next attempt then 403s (students cannot modify a completed application),
    // and exhausted network errors or 5xx can hide a successful write. Server
    // state is the source of truth — if the application reached the final
    // step, that's a success. (4xx validation failures can't have committed.)
    if (
      isFinalStep &&
      !isCreate &&
      (status === 403 || status === undefined || status >= 500)
    ) {
      try {
        const apps = await getApps()
        const submitted = apps?.find(
          app => app.id === appId && app.form?.step === FINAL_STEP,
        )
        if (submitted) {
          return submitted
        }
      } catch {
        // Recovery lookup failed — fall through to the original error.
      }
    }

    // A create can never succeed while the student has an active application
    // at the target term (unique constraint) — and a network-failed create may
    // even have committed. Whatever the failure status (409 conflict, pre-fix
    // duplicate-key 500, lost response), if a resumable draft exists at that
    // term, adopt it instead of failing.
    if (isCreate && termId && status !== 400 && status !== 403) {
      try {
        const apps = await getApps()
        const existing = apps?.find(app => app.termId === termId)
        if (existing && existing.form?.step !== FINAL_STEP) {
          return existing
        }
      } catch {
        // Fall through to the original error.
      }
    }

    // Conflicts and missing apps mean our local application state is stale
    // (another tab/device, or a submitted sibling app). Resync silently.
    if (status === 409 || status === 404) {
      onAppsChanged?.()
    }

    throw err
  }
}
